import { api, type AppRequestConfig } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { getMapSearchCity } from '@/lib/map-location'
import { getWorkerAvatarUrl } from '@/lib/worker-list-display'
import type { Worker, WorkerDocument, WorkerDocumentSection, WorkerVehicle, WorkerAttendance, PaginatedResponse, ApiResponse } from '@/types'

export type WorkerIdCard = {
  worker_id: number
  worker_code: string
  display_name: string
  qr_payload: string
  issued_at: string | null
}

type CreateWorkerAttendancePayload = {
  worker: string
  status?: boolean  // true=online, false=offline (optional, defaults to true)
  timestamp?: string  // ISO datetime (optional, defaults to current time)
  latitude?: number  // latitude (optional)
  longitude?: number  // longitude (optional)
  source?: 'mobile' | 'portal'
  job_location_id?: string | number
}

export interface WorkerSkill {
  id: string | number
  worker: string | number
  worker_detail?: Worker | null
  worker_display_name?: string | null
  skill_role: string | number
  skill_role_unique_id?: string | null
  skill_role_name?: string | null
  skill_role_title?: string | null
  job_category?: string | number | null
  job_category_name?: string | null
  job_category_title?: string | null
  pay_type?: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month' | null
  pay_type_label?: string | null
  additional_pay_mode?: 'hourly' | 'flat' | null
  additional_pay_max_hours?: string | number | null
  per_hour_pay_override?: string | number | null
  day_pay_override?: string | number | null
  night_pay_override?: string | number | null
  holiday_pay_override?: string | number | null
  overtime_pay_override?: string | number | null
  base_pay?: string | number | null
  night_pay?: string | number | null
  holiday_pay?: string | number | null
  peak_pay?: string | number | null
  overtime_pay?: string | number | null
  special_pay?: string | number | null
  active_from?: string | null
  active_to?: string | null
  is_primary?: boolean
  is_active?: boolean
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface WorkerSkillPayload {
  worker: string | number
  skill_role: string | number
  job_category?: string | number | null
  pay_type?: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month' | null
  pay_type_label?: string | null
  additional_pay_mode?: 'hourly' | 'flat' | null
  additional_pay_max_hours?: string | number | null
  per_hour_pay_override?: string | number | null
  day_pay_override?: string | number | null
  night_pay_override?: string | number | null
  holiday_pay_override?: string | number | null
  overtime_pay_override?: string | number | null
  base_pay?: string | number | null
  night_pay?: string | number | null
  holiday_pay?: string | number | null
  peak_pay?: string | number | null
  overtime_pay?: string | number | null
  special_pay?: string | number | null
  active_from?: string | null
  active_to?: string | null
  is_primary?: boolean
  is_active?: boolean
  notes?: string
}

export interface WorkerPayScale {
  id: string | number
  worker: string | number
  worker_skill?: string | number | null
  worker_skill_id?: string | number | null
  skill_role_name?: string | null
  pay_type: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month'
  pay_type_label?: string | null
  additional_pay_mode?: 'hourly' | 'flat'
  additional_pay_max_hours?: string | number | null
  base_pay: string | number
  night_pay: string | number
  holiday_pay: string | number
  peak_pay: string | number
  overtime_pay: string | number
  special_pay: string | number
  is_active?: boolean
  notes?: string
  created_at?: string
  updated_at?: string
}

export interface WorkerPayScalePayload {
  worker: string | number
  worker_skill?: string | number | null
  pay_type: 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month'
  pay_type_label?: string | null
  additional_pay_mode?: 'hourly' | 'flat'
  additional_pay_max_hours?: string | number | null
  base_pay: string | number
  night_pay: string | number
  holiday_pay: string | number
  peak_pay: string | number
  overtime_pay: string | number
  special_pay: string | number
  is_active?: boolean
  notes?: string
}

type RawWorker = Worker & {
  user?: string | number
  _id?: string | number
  user_data?: {
    fname?: string | null
    lname?: string | null
    email?: string | null
    phone_number?: string | null
    phn_nbr?: string | null
    mobile_number?: string | null
  } | null
  photo_url?: string | null
  mobile_number?: string | null
  status_label?: string | null
  worker_status?: string | number | null
  registration_status?: number | string | null
  registration_status_label?: string | null
  isOnline?: boolean
  last_lat?: number | string | null
  last_lan?: number | string | null
  last_location_text?: string | null
  last_updated_time?: string | null
}

const splitName = (value?: string) => {
  const parts = String(value ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { first: '', last: '' }
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}

const sanitizeWorkerWritePayload = (payload: Record<string, unknown>) => {
  const next = { ...payload }
  delete next.first_name
  delete next.last_name
  delete next.full_name
  delete next.photo_url
  return next
}

const normalizeApiWorkerStatus = (value: unknown): Worker['status'] | null => {
  const raw = String(value ?? '').trim().toLowerCase()
  if (!raw) return null
  
  // Handle UserRegistrationChoices numeric values:
  // 0 = Approved, 1 = Pending, 2 = Rejected, 3 = Unregistered, 4 = Suspended, 5 = Resigned
  if (raw === '0' || raw === 'approved') return 'active'
  if (raw === '1' || raw === 'pending') return 'pending'
  if (raw === '4' || raw === 'suspended') return 'suspended'
  if (raw === '2' || raw === '3' || raw === '5' || raw === 'rejected' || raw === 'unregistered' || raw === 'resigned' || raw === 'terminated' || raw === 'inactive') return 'inactive'
  
  // Fallback for other status formats
  if (raw === 'active') return 'active'
  
  return null
}

const normalizeWorker = (worker: Worker): Worker => {
  const rawWorker = worker as RawWorker
  const userRecord = rawWorker.user_data || null
  const userFirstName = String(userRecord?.fname ?? '').trim()
  const userLastName = String(userRecord?.lname ?? '').trim()
  const userFullName = [userFirstName, userLastName].filter(Boolean).join(' ').trim()
  const displayName = String(worker.display_name ?? '').trim()
    || String(worker.full_name ?? '').trim()
    || [String(worker.first_name ?? '').trim(), String(worker.last_name ?? '').trim()].filter(Boolean).join(' ').trim()
    || userFullName
    || `Worker ${worker.id}`
  const splitDisplayName = splitName(displayName)
  const firstName = String(worker.first_name ?? '').trim() || userFirstName || splitDisplayName.first
  const lastName = String(worker.last_name ?? '').trim() || userLastName || splitDisplayName.last
  const fullName = displayName
  const avatarSource = worker.avatar || worker.avatar_url || rawWorker.photo_url || undefined
  const avatar = getWorkerAvatarUrl({
    ...worker,
    avatar: avatarSource,
    avatar_url: worker.avatar_url || avatarSource,
    photo_url: rawWorker.photo_url,
  } as Worker)
  const statusSource = [rawWorker.registration_status, worker.status, rawWorker.worker_status, rawWorker.status_label]
    .find((candidate) => String(candidate ?? '').trim().length > 0)
  const status = normalizeApiWorkerStatus(statusSource) ?? 'inactive'

  const rawWorkerId = rawWorker.worker_id
    ?? rawWorker._id
    ?? unmaskMaskedId(rawWorker._id)
    ?? (typeof worker.id === 'number' ? worker.id : unmaskMaskedId(worker.id))

  return {
    ...worker,
    worker_id: rawWorkerId != null ? String(rawWorkerId) : undefined,
    worker_code: worker.worker_code ?? null,
    user_id: worker.user_id ?? (rawWorker.user !== undefined ? String(rawWorker.user) : String(worker.id)),
    display_name: displayName,
    first_name: firstName,
    last_name: lastName,
    full_name: fullName,
    email: worker.email || userRecord?.email || undefined,
    phone_number:
      worker.phone_number
      || worker.phone  // Use phone field if phone_number is not available
      || rawWorker.mobile_number
      || userRecord?.phone_number
      || userRecord?.mobile_number
      || userRecord?.phn_nbr
      || '',
    phone: worker.phone || worker.phone_number || '', // Ensure phone field is also available for forms
    address: worker.address ?? rawWorker.address ?? '',
    avatar,
    avatar_url: avatar || worker.avatar_url,
    skills: worker.skills ?? [],
    is_approved: worker.is_approved,
    is_online: worker.is_online ?? rawWorker.isOnline,
    isOnline: rawWorker.isOnline ?? worker.is_online,
    last_latitude: worker.last_latitude ?? rawWorker.last_lat,
    last_longitude: worker.last_longitude ?? rawWorker.last_lan,
    last_location_text: worker.last_location_text ?? rawWorker.last_location_text ?? '',
    last_location_updated_at: worker.last_location_updated_at ?? rawWorker.last_updated_time,
    permanent_latitude: worker.permanent_latitude ?? rawWorker.permanent_latitude ?? null,
    permanent_longitude: worker.permanent_longitude ?? rawWorker.permanent_longitude ?? null,
    permanent_location_text: worker.permanent_location_text ?? rawWorker.permanent_location_text ?? '',
    permanent_location_updated_at: worker.permanent_location_updated_at ?? rawWorker.permanent_location_updated_at ?? null,
    last_lat: rawWorker.last_lat ?? worker.last_latitude,
    last_lan: rawWorker.last_lan ?? worker.last_longitude,
    last_updated_time: rawWorker.last_updated_time ?? worker.last_location_updated_at,
    hotline_current_status: ((): Worker['hotline_current_status'] => {
      const normalized = String((worker as Worker).hotline_current_status ?? '').trim().toLowerCase()
      if (normalized === 'waiting' || normalized === 'approval' || normalized === 'denied' || normalized === 'exited') {
        return normalized
      }
      return null
    })(),
    hotline_joined_at: (worker as Worker).hotline_joined_at ?? null,
    hotline_exited_at: (worker as Worker).hotline_exited_at ?? null,
    hotline_additional_pay: (worker as Worker).hotline_additional_pay ?? null,
    hotline_is_active: Boolean((worker as Worker).hotline_is_active),
    status,
  }
}

export interface WorkerCreatePayload {
  post_id?: string | number
  user?: string | number
  contractor_user?: string | number
  display_name: string
  phone?: string
  email?: string
  first_name?: string
  last_name?: string
  role?: string | number
  categories?: Array<string | number>
  client?: string
  per_hour_pay?: number
  day_pay?: number
  night_pay?: number
  holiday_pay?: number
  overtime_pay?: number
  worker_type?: number
  verified?: boolean
  is_active?: boolean
  service_instance?: string
}

export interface WorkerCreationPost {
  id: string
  _id?: string | number | null
  post_id: string
  user_id?: string | null
  user_name?: string | null
  username?: string | null
  role_id?: string | null
  role_name?: string | null
  service_instance_id?: string | null
  service_instance_name?: string | null
  has_worker_profile?: boolean
  worker_id?: number | null
  status?: number | string
}

export interface WorkerMobileProfile {
  id: number
  display_name?: string
  registration_status?: number
  registration_status_label?: string
  role_id?: number | null
  role_name?: string | null
  post_id?: string | null
  service_instance_id?: string | null
  service_instance_name?: string | null
  is_selectable?: boolean
}

export interface WorkerFilters {
  page?: number
  page_size?: number
  search?: string
  ordering?: string
  kind?: 'job' | 'project' | 'event' | 'committee' | 'task'
  category?: string | number | Array<string | number>
  registration_status?: number
  status?: string
  active?: boolean
  worker_status?: string
  verified?: boolean
  attendance_status?: 'online' | 'offline'
  job_location_id?: string | number
  contractor_user?: string | number
  service_instance_id?: string | number
  hotline_status?: 'waiting' | 'approval' | 'denied' | 'exited'
  is_active?: boolean
  job_category?: string | number
  group_by_worker?: boolean
}

export interface WorkerImportFailure {
  row: number
  errors: Record<string, unknown>
}

export interface WorkerImportResult {
  created_count: number
  updated_count?: number
  failed_count: number
  created: Array<{
    row: number
    id?: string | number | null
    user?: string | number | null
  }>
  updated?: Array<{
    row: number
    id?: string | number | null
    user?: string | number | null
  }>
  failed: WorkerImportFailure[]
  message?: string
  total_rows?: number
  offset?: number
  limit?: number
  processed_count?: number
  has_more?: boolean
}

export type WorkerImportProgress = {
  totalRows: number
  processedRows: number
  createdCount: number
  updatedCount: number
  failedCount: number
  currentBatch: number
  totalBatches: number
  status: 'idle' | 'running' | 'completed' | 'failed'
  message?: string
}

export const WORKER_IMPORT_BATCH_SIZE = 5

const WORKER_IMPORT_FIELD_LABELS: Record<string, string> = {
  worker_code: 'Worker code',
  user_code: 'User code',
  display_name: 'Display name',
  phone: 'Phone',
  email: 'Email',
  role_code: 'Role code',
  worker_type: 'Worker type',
  contractor_code: 'Contractor code',
  category_codes: 'Category codes',
  categories: 'Categories',
  pay_type: 'Pay type',
  base_pay: 'Base pay',
  registration_status: 'Registration status',
  service_instance: 'Service instance',
  service_instance_id: 'Service instance',
  error: 'Error',
}

function labelWorkerImportField(field: string): string {
  return WORKER_IMPORT_FIELD_LABELS[field]
    ?? field.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

export function formatWorkerImportRowErrors(errors: Record<string, unknown>): string[] {
  const lines: string[] = []

  const append = (field: string, value: unknown) => {
    if (value == null) {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => append(field, item))
      return
    }

    if (typeof value === 'object') {
      const record = value as Record<string, unknown>
      if (typeof record.message === 'string' && record.message.trim()) {
        lines.push(`${labelWorkerImportField(field)}: ${record.message.trim()}`)
        return
      }

      for (const [nestedField, nestedValue] of Object.entries(record)) {
        append(nestedField, nestedValue)
      }
      return
    }

    const text = String(value).trim()
    if (text) {
      lines.push(`${labelWorkerImportField(field)}: ${text}`)
    }
  }

  for (const [field, value] of Object.entries(errors)) {
    append(field, value)
  }

  return lines
}

export function summarizeWorkerImportFailures(result: WorkerImportResult): string {
  if (result.failed.length === 0) {
    return result.message ?? `Imported ${result.created_count} workers successfully.`
  }

  const firstFailure = result.failed[0]
  const firstError = firstFailure
    ? formatWorkerImportRowErrors(firstFailure.errors)[0]
    : ''

  if (firstError && firstFailure) {
    return `Row ${firstFailure.row}: ${firstError}`
  }

  return result.message
    ?? `Imported ${result.created_count} workers. ${result.failed_count} rows failed.`
}

export const DEFAULT_WORKER_LIST_ORDERING = 'display_name,id'

export type WorkerHotlineStatus = 'waiting' | 'approval' | 'denied' | 'exited'

export interface WorkerHotlineEntry {
  id: string | number
  worker: string | number
  worker_name?: string | null
  service_instance: string | number
  status: WorkerHotlineStatus
  status_label?: string | null
  joined_at?: string | null
  exited_at?: string | null
  additional_pay?: string | number | null
  note?: string | null
  created_at?: string
  updated_at?: string
}

export interface WorkerLocationPayload {
  latitude: number
  longitude: number
  text?: string
  updatedAt?: string
}

export interface WorkerLocationSearchResult {
  id?: string | number | null
  label?: string | null
  lat?: string | number | null
  lon?: string | number | null
  door_no?: string | null
  street?: string | null
  city?: string | null
  zipcode?: string | null
}

export interface WorkerLocationSearchResponse {
  provider?: string | null
  configured_provider?: string | null
  provider_source?: string | null
  fallback_used?: boolean
  results?: WorkerLocationSearchResult[]
}

export type WorkerLeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export interface WorkerLeaveType {
  id: string | number
  name: string
  code: string
  description?: string | null
  paid?: boolean
  active?: boolean
}

export interface WorkerHoliday {
  id: string | number
  title: string
  start_date: string
  end_date: string
  description?: string | null
  active?: boolean
}

export interface WorkerLeaveRequest {
  id: string | number
  worker: string | number
  worker_name?: string | null
  leave_type: string | number
  leave_type_name?: string | null
  leave_type_code?: string | null
  start_date: string
  end_date: string
  reason?: string | null
  status: WorkerLeaveStatus
  day_count?: number
  approver_name?: string | null
  rejection_reason?: string | null
  created_at?: string
  updated_at?: string
}

export type AttendanceRequestType = 'missing_punch' | 'time_correction'
export type AttendanceRequestStatus = 'pending' | 'approved' | 'rejected'

export interface WorkerAttendanceRequest {
  id: string | number
  worker: string | number
  worker_name?: string | null
  attendance?: string | number | null
  work_date: string
  request_type: AttendanceRequestType
  requested_check_in?: string | null
  requested_check_out?: string | null
  reason?: string | null
  status: AttendanceRequestStatus
  rejection_reason?: string | null
  created_at?: string
}

export interface WorkerAttendanceRequestPayload {
  worker_id: string | number
  attendance_id?: string | number | null
  work_date: string
  request_type: AttendanceRequestType
  requested_check_in?: string | null
  requested_check_out?: string | null
  reason?: string
}

export interface WorkerLeaveRequestPayload {
  worker_id?: string | number
  leave_type: string | number
  start_date: string
  end_date: string
  reason?: string
}

export interface WorkerAvailabilityEntry {
  id?: string | number
  worker_id?: string | number
  date: string
  is_available: boolean
  from_time?: string | null
  to_time?: string | null
  notes?: string | null
}

export interface WorkerAvailabilityUpdatePayload {
  apply_weeks?: number
  entries: Array<{
    date: string
    is_available: boolean
    from_time?: string
    to_time?: string
    notes?: string
  }>
}

export interface WorkerDocumentSectionPayload {
  document_name: string
  type: number
  expire_on_date?: string
  country_code?: string | number
}

const roundWorkerCoordinate = (value: number): number => Number(value.toFixed(6))

async function fetchWorkerAttendanceList(params?: Record<string, unknown>): Promise<PaginatedResponse<WorkerAttendance>> {
  const { data } = await api.get<
    PaginatedResponse<WorkerAttendance> |
    ApiResponse<PaginatedResponse<WorkerAttendance>>
  >(API_PATHS.workers.attendance, { params })

  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data as PaginatedResponse<WorkerAttendance>
  }

  if (data && typeof data === 'object' && 'data' in data) {
    const nested = (data as ApiResponse<PaginatedResponse<WorkerAttendance>>).data
    if (nested && Array.isArray(nested.results)) {
      return nested
    }
  }

  return { count: 0, next: null, previous: null, results: [] }
}

function extractResults<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) {
    return payload as T[]
  }
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return extractResults<T>((payload as { data?: unknown }).data)
  }
  if (payload && typeof payload === 'object' && 'results' in payload) {
    const results = (payload as { results?: unknown }).results
    return Array.isArray(results) ? results as T[] : []
  }
  return []
}

export const workersApi = {
  // Worker Directory
  getWorkers: async (filters: WorkerFilters = {}, request?: { signal?: AbortSignal }): Promise<PaginatedResponse<Worker>> => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.search) params.append('search', filters.search)
    if (filters.ordering) params.append('ordering', filters.ordering)
    if (filters.kind) params.append('kind', filters.kind)
    if (filters.category !== undefined && filters.category !== null && filters.category !== '') {
      const categoryValue = Array.isArray(filters.category)
        ? filters.category.filter((entry) => String(entry).trim().length > 0).join(',')
        : String(filters.category)
      if (categoryValue) {
        params.append('category', categoryValue)
      }
    }
    if (filters.registration_status !== undefined) {
      params.append('registration_status', String(filters.registration_status))
    }
    if (filters.active !== undefined) params.append('active', String(filters.active))
    if (filters.worker_status ?? filters.status) params.append('worker_status', filters.worker_status ?? filters.status ?? '')
    if (filters.verified !== undefined) params.append('verified', String(filters.verified))
    if (filters.attendance_status) params.append('attendance_status', filters.attendance_status)
    if (filters.job_location_id !== undefined && filters.job_location_id !== '') {
      params.append('job_location_id', String(filters.job_location_id))
    }
    if (filters.contractor_user !== undefined && filters.contractor_user !== '') {
      params.append('contractor_user', String(filters.contractor_user))
    }
    if (filters.service_instance_id !== undefined && filters.service_instance_id !== '') {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    if (filters.hotline_status) params.append('hotline_status', filters.hotline_status)
    if (filters.is_active !== undefined) params.append('is_active', String(filters.is_active))
    if (filters.job_category !== undefined && filters.job_category !== '') {
      params.append('job_category', String(filters.job_category))
    }
    if (filters.group_by_worker !== undefined) params.append('group_by_worker', String(filters.group_by_worker))

    const { data } = await api.get<ApiResponse<PaginatedResponse<Worker>>>(API_PATHS.workers.list, {
      params,
      skipPostIdHeader: true,
      signal: request?.signal,
    } as AppRequestConfig)
    const result = data.data
    if (result?.results) {
      result.results = result.results.map(normalizeWorker)
    }
    return result
  },

  /** New token: previously printed cards stop working, the worker code is unchanged. */
  resetIdCard: async (id: string): Promise<WorkerIdCard> => {
    const { data } = await api.post<ApiResponse<WorkerIdCard>>(API_PATHS.workers.idCardReset(id))
    return data.data
  },

  resolveIdCard: async (scan: string): Promise<{ worker_id: number; worker_code: string; display_name: string }> => {
    const { data } = await api.get<ApiResponse<{ worker_id: number; worker_code: string; display_name: string }>>(
      API_PATHS.workers.idCardResolve,
      { params: { scan } },
    )
    return data.data
  },

  getWorker: async (id: string): Promise<Worker> => {
    const response = await api.get<ApiResponse<Worker> | Worker>(API_PATHS.workers.detail(id))
    const worker = 'data' in response.data ? response.data.data : response.data
    return normalizeWorker(worker)
  },

  createWorker: async (workerData: WorkerCreatePayload): Promise<Worker> => {
    const payload = sanitizeWorkerWritePayload(workerData as unknown as Record<string, unknown>)
    const { data } = await api.post<ApiResponse<Worker>>(API_PATHS.workers.create, payload)
    return data.data
  },

  importWorkers: async ({
    file,
    serviceInstanceId,
    offset,
    limit,
  }: {
    file: File
    serviceInstanceId?: string | null
    offset?: number
    limit?: number
  }): Promise<WorkerImportResult> => {
    const formData = new FormData()
    formData.append('file', file)
    if (serviceInstanceId) {
      formData.append('service_instance_id', serviceInstanceId)
    }
    if (typeof offset === 'number') {
      formData.append('offset', String(offset))
    }
    if (typeof limit === 'number') {
      formData.append('limit', String(limit))
    }

    const response = await api.post<ApiResponse<WorkerImportResult>>(
      API_PATHS.workers.import,
      formData,
      {
        params: {
          service_instance_id: serviceInstanceId || undefined,
          offset: typeof offset === 'number' ? offset : undefined,
          limit: typeof limit === 'number' ? limit : undefined,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120_000,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
      },
    )

    const payload = response.data
    const result = payload?.data
    if (result && typeof result === 'object' && 'failed_count' in result) {
      return {
        ...result,
        updated_count: Number(result.updated_count ?? result.updated?.length ?? 0),
        updated: Array.isArray(result.updated) ? result.updated : [],
        message: typeof payload.message === 'string' ? payload.message : undefined,
      }
    }

    throw new Error(
      typeof payload?.message === 'string' && payload.message.trim()
        ? payload.message
        : 'Worker import failed.',
    )
  },

  importWorkersInBatches: async ({
    file,
    serviceInstanceId,
    batchSize = WORKER_IMPORT_BATCH_SIZE,
    onProgress,
  }: {
    file: File
    serviceInstanceId?: string | null
    batchSize?: number
    onProgress?: (progress: WorkerImportProgress) => void
  }): Promise<WorkerImportResult> => {
    const aggregate: WorkerImportResult = {
      created_count: 0,
      updated_count: 0,
      failed_count: 0,
      created: [],
      updated: [],
      failed: [],
    }

    let offset = 0
    let totalRows = 0
    let batchNumber = 0
    let hasMore = true

    while (hasMore) {
      batchNumber += 1
      const batch = await workersApi.importWorkers({
        file,
        serviceInstanceId,
        offset,
        limit: batchSize,
      })

      totalRows = Number(batch.total_rows ?? totalRows)
      const processedCount = Number(batch.processed_count ?? batchSize)
      offset += processedCount
      hasMore = Boolean(batch.has_more)

      aggregate.created_count += Number(batch.created_count ?? 0)
      aggregate.updated_count = Number(aggregate.updated_count ?? 0) + Number(batch.updated_count ?? 0)
      aggregate.failed_count += Number(batch.failed_count ?? 0)
      aggregate.created.push(...(batch.created ?? []))
      aggregate.updated = [...(aggregate.updated ?? []), ...(batch.updated ?? [])]
      aggregate.failed.push(...(batch.failed ?? []))

      const processedRows = Math.min(offset, totalRows || offset)
      const totalBatches = totalRows > 0 ? Math.ceil(totalRows / batchSize) : batchNumber
      onProgress?.({
        totalRows: totalRows || processedRows,
        processedRows,
        createdCount: aggregate.created_count,
        updatedCount: Number(aggregate.updated_count ?? 0),
        failedCount: aggregate.failed_count,
        currentBatch: batchNumber,
        totalBatches,
        status: hasMore ? 'running' : 'completed',
        message: batch.message,
      })

      if (processedCount <= 0) {
        break
      }
    }

    aggregate.message = [
      `Worker import completed.`,
      `Created ${aggregate.created_count} row(s),`,
      `updated ${aggregate.updated_count ?? 0} row(s),`,
      `failed ${aggregate.failed_count} row(s)`,
      totalRows ? `(${Math.min(offset, totalRows)}/${totalRows}).` : '.',
    ].join(' ')

    return aggregate
  },

  getWorkerCreationPosts: async ({
    serviceInstanceId,
    eligibleOnly = true,
  }: {
    serviceInstanceId?: string | null
    eligibleOnly?: boolean
  }): Promise<WorkerCreationPost[]> => {
    const { data } = await api.get<ApiResponse<{ posts?: WorkerCreationPost[]; count?: number }>>(
      API_PATHS.workers.creationPosts,
      {
        params: {
          service_instance_id: serviceInstanceId ?? undefined,
          eligible_only: eligibleOnly ? 1 : undefined,
        },
      },
    )
    return data.data?.posts ?? []
  },

  getMyWorkerProfiles: async ({
    postId,
    includePending = true,
    serviceInstanceId,
  }: {
    postId: string | number
    includePending?: boolean
    serviceInstanceId?: string | null
  }): Promise<WorkerMobileProfile[]> => {
    const { data } = await api.get<ApiResponse<{ workers?: WorkerMobileProfile[] }>>(
      API_PATHS.workers.myProfiles,
      {
        params: {
          post_id: String(postId),
          include_pending: includePending ? 'true' : undefined,
        },
        headers: serviceInstanceId ? { 'service-instance-id': serviceInstanceId } : undefined,
      },
    )
    return data.data?.workers ?? []
  },

  updateWorker: async (id: string, workerData: Partial<Worker>): Promise<Worker> => {
    const payload = sanitizeWorkerWritePayload(workerData as Record<string, unknown>)
    const { data } = await api.post<ApiResponse<Worker>>(API_PATHS.workers.update(id), payload)
    return normalizeWorker(data.data)
  },

  setWorkerLocation: async (id: string, locationData: WorkerLocationPayload): Promise<Worker> => {
    return workersApi.updateWorker(id, {
      last_latitude: roundWorkerCoordinate(locationData.latitude),
      last_longitude: roundWorkerCoordinate(locationData.longitude),
      last_location_text: locationData.text?.trim() ?? '',
      last_location_updated_at: locationData.updatedAt ?? new Date().toISOString(),
    } as Partial<Worker>)
  },

  searchWorkerLocation: async (
    serviceInstanceId: string | number,
    query: string,
    limit = 5,
  ): Promise<WorkerLocationSearchResponse> => {
    const { data } = await api.get<ApiResponse<WorkerLocationSearchResponse> | WorkerLocationSearchResponse>(
      API_PATHS.workers.locationSearch(serviceInstanceId),
      {
        params: {
          q: query,
          limit,
        },
      },
    )
    const payload = 'data' in data ? data.data : data

    return {
      provider: payload?.provider ?? null,
      configured_provider: payload?.configured_provider ?? null,
      provider_source: payload?.provider_source ?? null,
      fallback_used: payload?.fallback_used ?? false,
      results: Array.isArray(payload?.results)
        ? payload.results.map((entry) => ({
            ...entry,
            city: getMapSearchCity(entry),
          }))
        : [],
    }
  },

  deleteWorker: async (id: string) => {
    await api.delete(API_PATHS.workers.delete(id))
  },

  // Worker Approvals
  getPendingApprovals: async (filters: WorkerFilters = {}): Promise<PaginatedResponse<Worker>> => {
    return workersApi.getWorkers({
      ...filters,
      worker_status: 'pending',
    })
  },

  approveWorker: async (id: string): Promise<ApiResponse<unknown>> => {
    const { data } = await api.post<ApiResponse<unknown>>(API_PATHS.workers.approve(id), {
      id,
      registration_status: 0,
    })
    return data
  },

  rejectWorker: async (id: string, reason?: string): Promise<ApiResponse<unknown>> => {
    const { data } = await api.post<ApiResponse<unknown>>(API_PATHS.workers.reject(id), {
      id,
      registration_status: 2,
      reason,
    })
    return data
  },

  updateWorkerStatus: async (id: string, registrationStatus: number): Promise<Worker> => {
    const { data } = await api.post<ApiResponse<Worker>>(API_PATHS.workers.update(id), {
      registration_status: registrationStatus,
    })
    return normalizeWorker(data.data)
  },

  // Worker Availability
  getWorkerAvailability: async (workerId: string, startDate: string, endDate: string): Promise<WorkerAvailabilityEntry[]> => {
    const { data } = await api.get<ApiResponse<{ results?: WorkerAvailabilityEntry[] }> | { results?: WorkerAvailabilityEntry[] }>(API_PATHS.workers.availabilityWeek, {
      params: {
        worker_id: workerId,
        start_date: startDate,
        end_date: endDate,
      },
    })
    return extractResults<WorkerAvailabilityEntry>(data)
  },

  updateWorkerAvailability: async (workerId: string, availabilityData: WorkerAvailabilityUpdatePayload): Promise<WorkerAvailabilityEntry[]> => {
    const { data } = await api.post<ApiResponse<{ results?: WorkerAvailabilityEntry[] }> | { results?: WorkerAvailabilityEntry[] }>(
      API_PATHS.workers.availabilityWeek,
      {
        worker_id: workerId,
        apply_weeks: availabilityData.apply_weeks,
        entries: availabilityData.entries,
      },
    )
    return extractResults<WorkerAvailabilityEntry>(data)
  },

  getWorkerLeaveTypes: async (): Promise<WorkerLeaveType[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerLeaveType[] }> | { results?: WorkerLeaveType[] }>(
      API_PATHS.workers.leaveTypes,
    )
    return extractResults<WorkerLeaveType>(response.data)
  },

  getAdminLeaveTypes: async (params?: { active?: boolean | string }): Promise<WorkerLeaveType[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerLeaveType[] }> | { results?: WorkerLeaveType[] }>(
      API_PATHS.workers.adminLeaveTypes,
      { params },
    )
    return extractResults<WorkerLeaveType>(response.data)
  },

  getWorkerHolidays: async (params?: { from_date?: string; to_date?: string }): Promise<WorkerHoliday[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerHoliday[] }> | { results?: WorkerHoliday[] }>(
      API_PATHS.workers.holidays,
      { params },
    )
    return extractResults<WorkerHoliday>(response.data)
  },

  getWorkerLeaveRequests: async (params?: { worker_id?: string | number; status?: WorkerLeaveStatus | string }): Promise<WorkerLeaveRequest[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerLeaveRequest[] }> | { results?: WorkerLeaveRequest[] }>(
      API_PATHS.workers.leaveRequests,
      { params },
    )
    return extractResults<WorkerLeaveRequest>(response.data)
  },

  createWorkerLeaveRequest: async (payload: WorkerLeaveRequestPayload): Promise<WorkerLeaveRequest> => {
    const { data } = await api.post<ApiResponse<{ leave_request?: WorkerLeaveRequest } | WorkerLeaveRequest>>(
      API_PATHS.workers.leaveRequests,
      payload,
    )
    const nested = data.data
    if (nested && typeof nested === 'object' && 'leave_request' in nested) {
      return nested.leave_request as WorkerLeaveRequest
    }
    return nested as WorkerLeaveRequest
  },

  cancelWorkerLeaveRequest: async (id: string | number, workerId?: string | number): Promise<WorkerLeaveRequest> => {
    const { data } = await api.post<ApiResponse<{ leave_request?: WorkerLeaveRequest } | WorkerLeaveRequest>>(
      API_PATHS.workers.leaveRequestCancel(id),
      workerId ? { worker_id: workerId } : {},
    )
    const nested = data.data
    if (nested && typeof nested === 'object' && 'leave_request' in nested) {
      return nested.leave_request as WorkerLeaveRequest
    }
    return nested as WorkerLeaveRequest
  },

  getAdminLeaveRequests: async (params?: { worker_id?: string | number; status?: WorkerLeaveStatus | string; on_date?: string }): Promise<WorkerLeaveRequest[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerLeaveRequest[] }> | { results?: WorkerLeaveRequest[] }>(
      API_PATHS.workers.adminLeaveRequests,
      { params },
    )
    return extractResults<WorkerLeaveRequest>(response.data)
  },

  createAdminLeaveRequest: async (payload: WorkerLeaveRequestPayload): Promise<WorkerLeaveRequest> => {
    const { data } = await api.post<ApiResponse<{ leave_request?: WorkerLeaveRequest } | WorkerLeaveRequest>>(
      API_PATHS.workers.adminLeaveRequests,
      payload,
    )
    const nested = data.data
    if (nested && typeof nested === 'object' && 'leave_request' in nested) {
      return nested.leave_request as WorkerLeaveRequest
    }
    return nested as WorkerLeaveRequest
  },

  approveAdminLeaveRequest: async (
    id: string | number,
    options?: { allow_schedule_clash?: boolean },
  ): Promise<WorkerLeaveRequest> => {
    const { data } = await api.post<ApiResponse<{ leave_request?: WorkerLeaveRequest } | WorkerLeaveRequest>>(
      API_PATHS.workers.adminLeaveRequestApprove(id),
      options?.allow_schedule_clash ? { allow_schedule_clash: true } : {},
    )
    const nested = data.data
    if (nested && typeof nested === 'object' && 'leave_request' in nested) {
      return nested.leave_request as WorkerLeaveRequest
    }
    return nested as WorkerLeaveRequest
  },

  rejectAdminLeaveRequest: async (id: string | number, reason?: string): Promise<WorkerLeaveRequest> => {
    const { data } = await api.post<ApiResponse<{ leave_request?: WorkerLeaveRequest } | WorkerLeaveRequest>>(
      API_PATHS.workers.adminLeaveRequestReject(id),
      reason ? { reason } : {},
    )
    const nested = data.data
    if (nested && typeof nested === 'object' && 'leave_request' in nested) {
      return nested.leave_request as WorkerLeaveRequest
    }
    return nested as WorkerLeaveRequest
  },

  getAdminAttendanceRequests: async (params?: { worker_id?: string | number; status?: AttendanceRequestStatus | string; work_date?: string }): Promise<WorkerAttendanceRequest[]> => {
    const response = await api.get<ApiResponse<{ results?: WorkerAttendanceRequest[] }> | { results?: WorkerAttendanceRequest[] }>(
      API_PATHS.workers.adminAttendanceRequests,
      { params },
    )
    return extractResults<WorkerAttendanceRequest>(response.data)
  },

  createAdminAttendanceRequest: async (payload: WorkerAttendanceRequestPayload): Promise<WorkerAttendanceRequest> => {
    const { data } = await api.post<ApiResponse<{ attendance_request?: WorkerAttendanceRequest }>>(
      API_PATHS.workers.adminAttendanceRequests,
      payload,
    )
    return data.data?.attendance_request as WorkerAttendanceRequest
  },

  approveAdminAttendanceRequest: async (id: string | number): Promise<WorkerAttendanceRequest> => {
    const { data } = await api.post<ApiResponse<{ attendance_request?: WorkerAttendanceRequest }>>(
      API_PATHS.workers.adminAttendanceRequestApprove(id),
      {},
    )
    return data.data?.attendance_request as WorkerAttendanceRequest
  },

  rejectAdminAttendanceRequest: async (id: string | number, reason?: string): Promise<WorkerAttendanceRequest> => {
    const { data } = await api.post<ApiResponse<{ attendance_request?: WorkerAttendanceRequest }>>(
      API_PATHS.workers.adminAttendanceRequestReject(id),
      reason ? { reason } : {},
    )
    return data.data?.attendance_request as WorkerAttendanceRequest
  },

  getHotlineEntries: async (params?: {
    service_instance_id?: string | number
    worker_id?: string | number
    status?: WorkerHotlineStatus
    active_only?: boolean
    page?: number
    page_size?: number
  }): Promise<PaginatedResponse<WorkerHotlineEntry>> => {
    const response = await api.get<
      PaginatedResponse<WorkerHotlineEntry> |
      ApiResponse<PaginatedResponse<WorkerHotlineEntry>>
    >(API_PATHS.workers.hotlineList, {
      params: {
        ...params,
        active_only: params?.active_only ? 'true' : undefined,
      },
    })
    const payload = 'data' in response.data ? response.data.data : response.data

    if (payload && typeof payload === 'object' && Array.isArray(payload.results)) {
      return {
        count: payload.count ?? payload.results.length,
        next: payload.next ?? null,
        previous: payload.previous ?? null,
        results: payload.results,
      }
    }

    return { count: 0, next: null, previous: null, results: [] }
  },

  addHotlineEntry: async (payload: {
    worker: string | number
    service_instance_id?: string | number
    status?: WorkerHotlineStatus
    additional_pay?: string | number | null
    note?: string
  }): Promise<WorkerHotlineEntry> => {
    const { data } = await api.post<ApiResponse<WorkerHotlineEntry> | WorkerHotlineEntry>(API_PATHS.workers.hotlineAdd, payload)
    return ('data' in data ? data.data : data) as WorkerHotlineEntry
  },

  updateHotlineEntry: async (
    id: string | number,
    payload: {
      status?: WorkerHotlineStatus
      additional_pay?: string | number | null
      note?: string
    },
  ): Promise<WorkerHotlineEntry> => {
    const { data } = await api.post<ApiResponse<WorkerHotlineEntry> | WorkerHotlineEntry>(API_PATHS.workers.hotlineUpdate(id), payload)
    return ('data' in data ? data.data : data) as WorkerHotlineEntry
  },

  exitHotlineEntry: async (payload: {
    worker: string | number
    service_instance_id?: string | number
  }): Promise<WorkerHotlineEntry> => {
    const { data } = await api.post<ApiResponse<WorkerHotlineEntry> | WorkerHotlineEntry>(API_PATHS.workers.hotlineExit, payload)
    return ('data' in data ? data.data : data) as WorkerHotlineEntry
  },

  // Worker Documents
  getWorkerDocuments: async (workerId: string): Promise<WorkerDocument[]> => {
    const response = await api.get<
      PaginatedResponse<WorkerDocument> |
      ApiResponse<PaginatedResponse<WorkerDocument>> |
      ApiResponse<WorkerDocument[]>
    >(API_PATHS.workers.documents(workerId), {
      params: { worker: workerId },
    })
    const payload = 'data' in response.data ? response.data.data : response.data

    if (Array.isArray(payload)) {
      return payload
    }

    return payload.results ?? []
  },

  getWorkerDocumentSections: async (params?: { type?: number; search_value?: string; service_instance_id?: string | number }): Promise<WorkerDocumentSection[]> => {
    const response = await api.get<
      PaginatedResponse<WorkerDocumentSection> |
      ApiResponse<PaginatedResponse<WorkerDocumentSection>> |
      ApiResponse<WorkerDocumentSection[]>
    >(API_PATHS.workers.documentSections, {
      params,
    })

    const payload = 'data' in response.data ? response.data.data : response.data

    if (Array.isArray(payload)) {
      return payload
    }

    return payload.results ?? []
  },

  createWorkerDocumentSection: async (
    payload: WorkerDocumentSectionPayload,
    options?: { service_instance_id?: string | number },
  ): Promise<WorkerDocumentSection> => {
    const { data } = await api.post<ApiResponse<WorkerDocumentSection>>(
      API_PATHS.workers.documentSectionCreate,
      payload,
      { params: options },
    )
    return data.data
  },

  updateWorkerDocumentSection: async (
    sectionId: string,
    payload: Partial<WorkerDocumentSectionPayload>,
    options?: { service_instance_id?: string | number },
  ): Promise<WorkerDocumentSection> => {
    const { data } = await api.post<ApiResponse<WorkerDocumentSection>>(
      API_PATHS.workers.documentSectionUpdate(sectionId),
      payload,
      { params: options },
    )
    return data.data
  },

  deleteWorkerDocumentSection: async (
    sectionId: string,
    options?: { service_instance_id?: string | number },
  ): Promise<void> => {
    await api.delete(API_PATHS.workers.documentSectionDelete(sectionId), {
      params: options,
    })
  },

  uploadWorkerDocument: async (workerId: string, formData: FormData): Promise<WorkerDocument> => {
    if (!formData.has('worker')) {
      formData.append('worker', workerId)
    }

    const { data } = await api.post<ApiResponse<WorkerDocument>>(API_PATHS.workers.documentCreate, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return data.data
  },

  createWorkerDocumentWithFiles: async (payload: {
    workerId: string
    sectionId: string
    identNo?: string
    effectiveFrom?: string
    effectiveTo?: string
    expiredDate?: string
    frontFile?: File | null
    backFile?: File | null
  }): Promise<WorkerDocument> => {
    const formData = new FormData()
    formData.append('worker', payload.workerId)
    formData.append('worker_doc_section', payload.sectionId)
    formData.append('type', '1')

    if (payload.identNo) formData.append('ident_no', payload.identNo)
    if (payload.effectiveFrom) formData.append('effective_from', payload.effectiveFrom)
    if (payload.effectiveTo) formData.append('effective_to', payload.effectiveTo)
    if (payload.expiredDate) formData.append('expired_date', payload.expiredDate)
    if (payload.frontFile) formData.append('document_front', payload.frontFile)
    if (payload.backFile) formData.append('document_back', payload.backFile)

    return workersApi.uploadWorkerDocument(payload.workerId, formData)
  },

  deleteWorkerDocument: async (_workerId: string, documentId: string) => {
    await api.delete(API_PATHS.workers.documentDelete(documentId))
  },

  // Worker Vehicles
  getWorkerVehicles: async (_workerId: string): Promise<WorkerVehicle[]> => {
    return []
  },

  addWorkerVehicle: async (_workerId: string, vehicleData: Partial<WorkerVehicle>): Promise<WorkerVehicle> => {
    const { data } = await api.post<ApiResponse<WorkerVehicle>>(API_PATHS.workers.vehiclesCreate, vehicleData)
    return data.data
  },

  updateWorkerVehicle: async (_workerId: string, _vehicleId: string, _vehicleData: Partial<WorkerVehicle>): Promise<WorkerVehicle> => {
    throw new Error('Worker vehicle updates are not exposed by the backend.')
  },

  deleteWorkerVehicle: async (_workerId: string, vehicleId: string) => {
    await api.delete(API_PATHS.workers.vehicleDelete(vehicleId))
  },

  // Worker Attendance
  getWorkerAttendance: async (workerId: string, params?: Record<string, unknown>): Promise<WorkerAttendance[]> => {
    const response = await fetchWorkerAttendanceList({
      ...(params || {}),
      worker: workerId,
    })
    return response.results ?? []
  },

  getWorkerAttendanceList: fetchWorkerAttendanceList,

  createWorkerAttendance: async (attendanceData: CreateWorkerAttendancePayload): Promise<WorkerAttendance> => {
    const { data } = await api.post<ApiResponse<WorkerAttendance> | WorkerAttendance>(API_PATHS.workers.attendanceCreate, attendanceData)
    if (data && typeof data === 'object' && 'data' in data && data.data) {
      return data.data
    }
    return data as WorkerAttendance
  },

  closeWorkerAttendance: async (workerId: string): Promise<WorkerAttendance> => {
    return workersApi.createWorkerAttendance({
      worker: workerId,
      status: false,
      source: 'portal',
    })
  },

  deleteWorkerAttendance: async (attendanceId: string | number): Promise<void> => {
    await api.delete(API_PATHS.workers.attendanceDelete(attendanceId))
  },

  // Worker Skills - fetches grouped response from API but returns flat array for component compatibility
  getWorkerSkills: async (params?: {
    worker?: string | number
    skill_role?: string | number
    job_category?: string | number
    is_active?: boolean
    service_instance_id?: string | number
    search?: string
    page?: number
    page_size?: number
    group_by_worker?: boolean
  }): Promise<WorkerSkill[] | any[]> => {
    // Always request grouped format from API (includes worker_detail field)
    const requestParams = {
      ...params,
      group_by_worker: true
    }
    
    const response = await api.get<
      PaginatedResponse<WorkerSkill> |
      ApiResponse<PaginatedResponse<WorkerSkill>> |
      ApiResponse<{ status: string; data: any[]; count: number }> |
      { status: string; data: any[]; count: number } |
      ApiResponse<WorkerSkill[]> |
      WorkerSkill[]
    >(API_PATHS.workers.workerSkillsList, { params: requestParams })

    const payload = response.data as unknown
    
    // Helper: flatten grouped response to individual skill records
    const flattenGroupedResponse = (groups: any[]): WorkerSkill[] => {
      const result: WorkerSkill[] = []
      for (const group of groups) {
        if (Array.isArray(group.skills)) {
          // Each skill already has all needed data including worker_detail
          result.push(...group.skills)
        }
      }
      return result
    }
    
    // Handle grouped response format in ApiResponse wrapper
    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      const apiPayload = payload as ApiResponse<any>
      if (Array.isArray(apiPayload.data)) {
        // Check if this is grouped format (has worker_detail and skills fields)
        if (apiPayload.data[0]?.worker_detail && Array.isArray(apiPayload.data[0]?.skills)) {
          return flattenGroupedResponse(apiPayload.data)
        }
        return apiPayload.data
      }
    }
    
    // Handle direct grouped response (status + data + count)
    if (typeof payload === 'object' && payload !== null && 'status' in payload) {
      const groupedPayload = payload as { status: string; data: any[]; count: number }
      if (Array.isArray(groupedPayload.data)) {
        if (groupedPayload.data[0]?.worker_detail && Array.isArray(groupedPayload.data[0]?.skills)) {
          return flattenGroupedResponse(groupedPayload.data)
        }
        return groupedPayload.data
      }
    }
    
    // Fallback: handle flat array response
    if (Array.isArray(payload)) {
      return payload
    }

    if (typeof payload === 'object' && payload !== null && 'results' in payload) {
      return (payload as PaginatedResponse<WorkerSkill>).results ?? []
    }

    return []
  },

  getWorkerSkill: async (id: string | number): Promise<WorkerSkill> => {
    const response = await api.get<ApiResponse<WorkerSkill> | WorkerSkill>(API_PATHS.workers.workerSkillsGet(id))
    const raw = response.data
    return typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw
  },

  getSkillsByWorker: async (workerId: string | number, params?: {
    service_instance_id?: string | number
    is_active?: boolean
  }): Promise<WorkerSkill[]> => {
    const requestParams: Record<string, string | number | boolean> = {
      worker: workerId,
    }

    if (params?.service_instance_id != null && String(params.service_instance_id).trim()) {
      requestParams.service_instance_id = params.service_instance_id
    }

    if (typeof params?.is_active === 'boolean') {
      requestParams.is_active = params.is_active
    }

    const response = await api.get<
      PaginatedResponse<WorkerSkill> |
      ApiResponse<PaginatedResponse<WorkerSkill>> |
      ApiResponse<WorkerSkill[]> |
      WorkerSkill[]
    >(API_PATHS.workers.workerSkillsList, { params: requestParams })

    const payload = response.data as unknown

    if (Array.isArray(payload)) {
      return payload
    }

    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      const dataPayload = (payload as ApiResponse<PaginatedResponse<WorkerSkill> | WorkerSkill[]>).data
      if (Array.isArray(dataPayload)) {
        return dataPayload
      }
      if (typeof dataPayload === 'object' && dataPayload !== null && 'results' in dataPayload) {
        return dataPayload.results ?? []
      }
      return []
    }

    if (typeof payload === 'object' && payload !== null && 'results' in payload) {
      return (payload as PaginatedResponse<WorkerSkill>).results ?? []
    }

    return []
  },

  createWorkerSkill: async (payload: WorkerSkillPayload): Promise<WorkerSkill> => {
    const response = await api.post<ApiResponse<WorkerSkill> | WorkerSkill>(API_PATHS.workers.workerSkillsCreate, payload)
    const raw = response.data
    return typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw
  },

  updateWorkerSkill: async (id: string | number, payload: Partial<WorkerSkillPayload>): Promise<WorkerSkill> => {
    const response = await api.post<ApiResponse<WorkerSkill> | WorkerSkill>(API_PATHS.workers.workerSkillsUpdate(id), payload)
    const raw = response.data
    return typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw
  },

  deleteWorkerSkill: async (id: string | number): Promise<void> => {
    await api.delete(API_PATHS.workers.workerSkillsDelete(id))
  },

  // Worker Pay Scales
  getWorkerPayScales: async (params?: {
    worker?: string | number
    worker_skill?: string | number
    pay_type?: string
    is_active?: boolean
    service_instance_id?: string | number
    page?: number
    page_size?: number
  }): Promise<WorkerPayScale[]> => {
    const response = await api.get<
      PaginatedResponse<WorkerPayScale> |
      ApiResponse<PaginatedResponse<WorkerPayScale>> |
      ApiResponse<WorkerPayScale[]> |
      WorkerPayScale[]
    >(API_PATHS.workers.workerPayScalesList, { params })

    const payload = response.data as unknown
    if (Array.isArray(payload)) {
      return payload
    }

    if (typeof payload === 'object' && payload !== null && 'data' in payload) {
      const dataPayload = (payload as ApiResponse<PaginatedResponse<WorkerPayScale> | WorkerPayScale[]>).data
      if (Array.isArray(dataPayload)) {
        return dataPayload
      }
      return dataPayload?.results ?? []
    }

    if (typeof payload === 'object' && payload !== null && 'results' in payload) {
      return (payload as PaginatedResponse<WorkerPayScale>).results ?? []
    }

    return []
  },

  createWorkerPayScale: async (payload: WorkerPayScalePayload): Promise<WorkerPayScale> => {
    const response = await api.post<ApiResponse<WorkerPayScale> | WorkerPayScale>(API_PATHS.workers.workerPayScalesCreate, payload)
    const raw = response.data
    return typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw
  },

  updateWorkerPayScale: async (id: string | number, payload: Partial<WorkerPayScalePayload>): Promise<WorkerPayScale> => {
    const response = await api.post<ApiResponse<WorkerPayScale> | WorkerPayScale>(API_PATHS.workers.workerPayScalesUpdate(id), payload)
    const raw = response.data
    return typeof raw === 'object' && raw !== null && 'data' in raw ? raw.data : raw
  },

  deleteWorkerPayScale: async (id: string | number): Promise<void> => {
    await api.delete(API_PATHS.workers.workerPayScalesDelete(id))
  },
}

/**
 * Parse CSV content into an array of row objects
 * @param content CSV text content
 * @returns Array of objects with column names as keys
 */
export function parseCSVContent(content: string): Array<Record<string, string>> {
  const lines = content.split(/\r?\n/).filter((line) => line.trim())
  if (lines.length === 0) return []

  // Parse header
  const header = parseCSVLine(lines[0])
  if (header.length === 0) return []

  // Parse rows
  const rows: Array<Record<string, string>> = []
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i])
    const row: Record<string, string> = {}
    header.forEach((key, index) => {
      row[key] = values[index] ?? ''
    })
    rows.push(row)
  }

  return rows
}

/**
 * Parse a single CSV line handling quoted values
 * @param line CSV line to parse
 * @returns Array of parsed values
 */
function parseCSVLine(line: string): string[] {
  const values: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    const nextChar = line[i + 1]

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }

  values.push(current.trim())
  return values
}

/**
 * Convert array of row objects to CSV string
 * @param rows Array of objects with column names as keys
 * @param rowNumbers Optional array of row numbers to include (1-indexed)
 * @returns CSV string
 */
export function convertRowsToCSV(
  rows: Array<Record<string, string>>,
  rowNumbers?: number[],
): string {
  if (rows.length === 0) return ''

  const rowsToExport = rowNumbers
    ? rowNumbers.map((num) => rows[num - 1]).filter(Boolean)
    : rows

  if (rowsToExport.length === 0) return ''

  // Get all unique column names
  const headers = Array.from(
    new Set(rowsToExport.flatMap((row) => Object.keys(row))),
  )

  // Build CSV
  const csvLines: string[] = [
    // Header
    headers.map((h) => escapeCSVValue(h)).join(','),
    // Rows
    ...rowsToExport.map((row) =>
      headers.map((header) => escapeCSVValue(row[header] ?? '')).join(','),
    ),
  ]

  return csvLines.join('\n')
}

/**
 * Escape CSV value if needed (handle quotes and commas)
 * @param value String value to escape
 * @returns Escaped CSV value
 */
function escapeCSVValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"` 
  }
  return value
}

/**
 * Trigger download of CSV file
 * @param content CSV content to download
 * @param filename Name of the file to download
 */
export function downloadCSV(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}
